import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { ConsignationService } from '@beautyapp/core';
import { TranslatePipe } from '@ngx-translate/core';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';

/**
 * Per-customer consignations tab inside the user drawer.
 *
 * Intentionally lives in `libs/users` and talks directly to
 * `ConsignationService` (from `@beautyapp/core`). The global
 * `libs/consignation` lib already imports `@beautyapp/users` to
 * open `UserViewComponent` — importing back would be a circular
 * dependency, so we do not reuse `ConsignationsComponent` or
 * `ConsignationPayComponent` here. This tab is read-only;
 * staff can still pay via the existing global consignations page.
 *
 * The backend `GET /consignations` already filters by `search.userId`
 * (see `ConsignationsComponent.dateform.userId` in the old list
 * view), which is the only knob we need for the scoped view.
 */
interface ConsignationRow {
  id: number;
  status: number;
  price: number;
  salePrice: number;
  cash: number;
  card: number;
  consignation: number;
  consignationPayed: number;
  createdAt: string;
  operation?: { operationDate?: string; operationSource?: string };
  employee?: { user?: { fullName?: string } };
}

@Component({
  selector: 'beautyapp-user-consignations',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    DatePipe,
    TranslatePipe,
    NzTableModule,
    NzTagModule,
    NzEmptyModule,
    NzIconModule,
    NzSpinModule,
    NzStatisticModule,
  ],
  template: `
    <div class="consignations">
      <header class="summary">
        <div class="summary__tile summary__tile--open">
          <span nz-icon nzType="exclamation-circle" class="summary__icon"></span>
          <div>
            <div class="summary__label">
              {{ 'statistic.consignation.search.toPaid' | translate }}
            </div>
            <div class="summary__value">
              {{ openBalance() | number: '1.2-2' }}
              <small>{{ 'users.gel' | translate }}</small>
            </div>
          </div>
        </div>
        <div class="summary__tile summary__tile--paid">
          <span nz-icon nzType="check-circle" class="summary__icon"></span>
          <div>
            <div class="summary__label">
              {{ 'statistic.consignation.search.paid' | translate }}
            </div>
            <div class="summary__value">
              {{ paidBalance() | number: '1.2-2' }}
              <small>{{ 'users.gel' | translate }}</small>
            </div>
          </div>
        </div>
        <div class="summary__tile summary__tile--count">
          <span nz-icon nzType="file-text" class="summary__icon"></span>
          <div>
            <div class="summary__label">
              {{ 'statistic.consignation.search.totalPayment' | translate }}
            </div>
            <div class="summary__value">
              {{ rows().length }}
            </div>
          </div>
        </div>
      </header>

      @if (loading()) {
        <div class="state"><nz-spin nzSimple></nz-spin></div>
      } @else if (!rows().length) {
        <nz-empty class="state"></nz-empty>
      } @else {
        <nz-table
          [nzData]="rows()"
          [nzFrontPagination]="false"
          nzSize="small"
          nzBordered
        >
          <thead>
            <tr>
              <th>{{ 'statistic.consignation.column.date' | translate }}</th>
              <th>{{ 'statistic.consignation.column.price' | translate }}</th>
              <th>{{ 'statistic.consignation.column.consignment' | translate }}</th>
              <th>{{ 'statistic.consignation.column.covered' | translate }}</th>
              <th>{{ 'statistic.consignation.column.cash' | translate }}</th>
              <th>{{ 'statistic.consignation.column.card' | translate }}</th>
              <th>{{ 'statistic.consignation.column.employee' | translate }}</th>
              <th>{{ 'statistic.consignation.column.status' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            @for (row of rows(); track row.id) {
              <tr>
                <td>{{ row.operation?.operationDate | date: 'dd/MM/yyyy' }}</td>
                <td>{{ row.price | number: '1.2-2' }}</td>
                <td>{{ row.consignation | number: '1.2-2' }}</td>
                <td>{{ row.consignationPayed | number: '1.2-2' }}</td>
                <td>{{ row.cash | number: '1.2-2' }}</td>
                <td>{{ row.card | number: '1.2-2' }}</td>
                <td>{{ row.employee?.user?.fullName || '—' }}</td>
                <td>
                  @if (row.status === 1) {
                    <nz-tag nzColor="volcano">
                      {{ 'statistic.consignation.search.toPaid' | translate }}
                    </nz-tag>
                  } @else {
                    <nz-tag nzColor="green">
                      {{ 'statistic.consignation.search.paid' | translate }}
                    </nz-tag>
                  }
                </td>
              </tr>
            }
          </tbody>
        </nz-table>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }
    .consignations {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 4px 2px 24px;
    }
    .summary {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 12px;
    }
    .summary__tile {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 16px;
      border-radius: 12px;
      border: 1px solid #e5e7eb;
      background: #fff;
      box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
    }
    .summary__icon {
      font-size: 20px;
      padding: 10px;
      border-radius: 10px;
      display: inline-flex;
    }
    .summary__label {
      font-size: 11.5px;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      color: #94a3b8;
    }
    .summary__value {
      font-size: 17px;
      font-weight: 600;
      color: #0f172a;
      line-height: 1.1;
    }
    .summary__value small {
      font-size: 11px;
      color: #64748b;
      margin-left: 4px;
      font-weight: 500;
    }
    .summary__tile--open .summary__icon {
      background: #fff1f2;
      color: #e11d48;
    }
    .summary__tile--paid .summary__icon {
      background: #ecfdf5;
      color: #059669;
    }
    .summary__tile--count .summary__icon {
      background: #eff6ff;
      color: #2563eb;
    }
    .state {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 40px 0;
    }
    @media (max-width: 720px) {
      .summary { grid-template-columns: 1fr; }
    }
  `],
})
export class UserConsignationsComponent {
  userId = input.required<string>();

  private api = inject(ConsignationService);

  rows = signal<ConsignationRow[]>([]);
  loading = signal(false);

  openBalance = computed(() =>
    this.rows()
      .filter((r) => r.status === 1)
      .reduce((acc, r) => acc + (+r.consignation - +r.consignationPayed), 0),
  );

  paidBalance = computed(() =>
    this.rows().reduce((acc, r) => acc + +(r.consignationPayed || 0), 0),
  );

  constructor() {
    effect(() => {
      const id = this.userId();
      if (id) this.load(id);
    });
  }

  private load(userId: string): void {
    this.loading.set(true);
    this.api
      .get({
        search: { userId },
        page: 1,
        pageSize: 200,
        isDeleted: false,
      })
      .subscribe({
        next: (res: any) => {
          this.rows.set((res?.data ?? []) as ConsignationRow[]);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }
}
